(() => {
  if (window.CAShippingBoxGuard) return;
  const BOX_PRODUCT_ID = 8216362287330;
  const BOX_VARIANT_ID = 44515284222178;
  const METHOD_SELECTOR = '#birdchime-slots-box .bcdp-method-active';
  let observedMethod = '';
  let methodVersion = 0;
  const originalFetch = window.fetch.bind(window);
  const mutationPattern = /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/i;
  const checkoutSelector = '[name="checkout"], a[href*="/checkout"], [data-ca-bird-continue]';
  const replaying = new WeakSet();
  let queue = Promise.resolve();
  let pendingMutations = 0;
  let lastMutation = 0;
  let mutationVersion = 0;
  let timer;
  let checkoutBusy = false;
  const pause = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
  const root = () => window.Shopify?.routes?.root || '/';

  async function request(path, body) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    try {
      const response = await originalFetch(root() + path, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      if (!response.ok) throw new Error('Could not verify the shipping box quantity. Please try again.');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('Could not read the cart. Please try again.');
      return cart;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async function waitForCart() {
    const deadline = Date.now() + 8000;
    while (pendingMutations || Date.now() - lastMutation < 250) {
      if (Date.now() >= deadline) throw new Error('Your cart is still updating. Please try checkout again.');
      await pause(50);
    }
  }

  function normalizeMethod(value) {
    const method = String(value || '').trim().toLowerCase();
    if (method === 'pick up' || method === 'pickup' || method === 'local pickup') return 'Pick Up';
    if (method === 'shipping') return 'Shipping';
    if (method === 'delivery' || method === 'local delivery') return 'Delivery';
    return '';
  }

  function widgetMethod() {
    return normalizeMethod(document.querySelector(METHOD_SELECTOR)?.textContent);
  }

  function hasCustomerItems(cart) {
    return cart.items.some((item) => {
      if (Number(item.product_id) === BOX_PRODUCT_ID || Number(item.quantity) <= 0) return false;
      const title = String(item.product_title || item.title || '').toLowerCase();
      return !title.includes('shipping protection') && !title.includes('insurance');
    });
  }

  function notifyCart(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
    const drawer = document.querySelector('#MinimogCartDrawer');
    const modalOpen = document.querySelector('[data-ca-bird-overlay]')?.classList.contains('ca-bird-delivery-overlay--active');
    if (typeof drawer?.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate(!modalOpen);
    if (typeof window.CA_SYNC_CART_DRAWER_STATE === 'function') window.CA_SYNC_CART_DRAWER_STATE(0);
  }

  async function normalize() {
    let changed = false;
    for (let attempt = 0; attempt < 12; attempt++) {
      await waitForCart();
      const version = mutationVersion;
      const selectionVersion = methodVersion;
      const cart = await request('cart.js');
      if (pendingMutations || mutationVersion !== version || methodVersion !== selectionVersion) continue;
      const selected = widgetMethod();
      const saved = normalizeMethod(cart.attributes?.['Delivery Method']);
      const method = selected || saved;
      if (selected && saved !== selected) {
        await request('cart/update.js', { attributes: { 'Delivery Method': selected } });
        changed = true;
        continue;
      }
      const boxes = cart.items.filter((item) => Number(item.product_id) === BOX_PRODUCT_ID && Number(item.quantity) > 0);
      const total = boxes.reduce((sum, item) => sum + Number(item.quantity), 0);
      const desired = method === 'Pick Up' || !hasCustomerItems(cart) ? 0 : 1;
      if (total === desired) {
        if (changed) notifyCart(cart);
        return cart;
      }
      if (!boxes.length && desired === 1) {
        await request('cart/add.js', { items: [{ id: BOX_VARIANT_ID, quantity: 1 }] });
      } else {
        const target = boxes[boxes.length - 1];
        await request('cart/change.js', { id: target.key, quantity: desired === 0 || boxes.length > 1 ? 0 : 1 });
      }
      changed = true;
    }
    throw new Error('Could not update the shipping box for your delivery method. Please refresh your cart and try again.');
  }

  function ensure() {
    const next = queue.then(normalize, normalize);
    queue = next.catch(() => {});
    return next;
  }

  function schedule() {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => ensure().catch((error) => console.warn('[CA Shipping Box]', error.message)), 300);
  }

  function started() {
    pendingMutations++;
    mutationVersion++;
    lastMutation = Date.now();
  }

  function finished() {
    pendingMutations--;
    mutationVersion++;
    lastMutation = Date.now();
    schedule();
  }

  window.CAShippingBoxGuard = { ensure };
  window.fetch = function (input, options) {
    const url = typeof input === 'string' ? input : input?.url || String(input);
    if (!mutationPattern.test(url)) return originalFetch(input, options);
    started();
    try {
      const result = originalFetch(input, options);
      result.then(finished, finished);
      return result;
    } catch (error) {
      finished();
      throw error;
    }
  };

  if (window.XMLHttpRequest) {
    const open = XMLHttpRequest.prototype.open;
    const send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.caBoxMutation = mutationPattern.test(String(url));
      return open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (!this.caBoxMutation) return send.apply(this, arguments);
      started();
      const done = () => finished();
      this.addEventListener('loadend', done, { once: true });
      try {
        return send.apply(this, arguments);
      } catch (error) {
        this.removeEventListener('loadend', done);
        finished();
        throw error;
      }
    };
  }

  async function gate(event, target, replay) {
    if (replaying.has(target)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutBusy) return;
    checkoutBusy = true;
    try {
      await ensure();
      replaying.add(target);
      try { replay(); } finally { replaying.delete(target); }
    } catch (error) {
      window.alert(error.message);
    } finally {
      checkoutBusy = false;
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target.closest?.(checkoutSelector);
    if (target) gate(event, target, () => target.click());
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (replaying.has(event.submitter)) return;
    const isCheckout = event.submitter?.name === 'checkout' || /\/checkout\/?$/.test(new URL(form.action, window.location.href).pathname);
    if (isCheckout) gate(event, form, () => form.requestSubmit(event.submitter || undefined));
  }, true);

  function observeDeliveryMethod() {
    const check = () => {
      const method = widgetMethod();
      if (!method || method === observedMethod) return;
      observedMethod = method;
      methodVersion++;
      schedule();
    };
    new MutationObserver(check).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    check();
    schedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeDeliveryMethod, { once: true });
  else observeDeliveryMethod();
})();
